// Client-side /draft prospect board. Queries the expansion_prospects table
// (from_eligibility=1) from the browser DB and renders the summary, the
// position/league controls, and one sortable/filterable table per position
// group — mirroring templates/draft.html. Pure logic (grouping, ranking, stat
// cells, league/summary counts) lives in draft-logic.ts (parity-tested in Node).

import { getDbWorker } from "./db";
import {
  GOALIE_COLUMNS,
  POSITION_GROUPS,
  type Prospect,
  SKATER_COLUMNS,
  STAT_HEADER_SLUGS,
  byPosition,
  fullName,
  isTopProspect,
  leagueName,
  leaguesPresent,
  statCells,
  summarize,
} from "./draft-logic";

function esc(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function gloss(slug: string): string {
  const g = (window as unknown as { __PWHL_GLOSS__?: Record<string, string> }).__PWHL_GLOSS__;
  return (g && g[slug]) || "";
}

function renderControls(all: Prospect[], total: number): string {
  const posOpts = POSITION_GROUPS.map(([code, title]) =>
    `<option value="${code}">${esc(title)} (${byPosition(all, code).length})</option>`).join("");
  const leagueOpts = leaguesPresent(all).map(([code, name, , n]) =>
    `<option value="${esc(code)}">${esc(name)} (${n})</option>`).join("");
  return `
    <label class="draft-filter"><span>Position</span>
      <select id="draft-position-filter"><option value="">All positions</option>${posOpts}</select>
    </label>
    <label class="draft-filter"><span>League</span>
      <select id="draft-league-filter"><option value="">All leagues (${total})</option>${leagueOpts}</select>
    </label>
    <p class="draft-hint"><span class="draft-top-badge">★</span> = top prospect.
      Click a column header to sort. Stats are 2025-26 regular season.</p>`;
}

function renderGroup(code: string, title: string, members: Prospect[]): string {
  const cols = code === "G" ? GOALIE_COLUMNS : SKATER_COLUMNS;
  const statHead = cols.map(([, h]) => {
    const slug = STAT_HEADER_SLUGS[h] ?? "";
    const g = slug ? gloss(slug) : "";
    return `<th class="draft-col-stat${g ? " gloss-th" : ""}" data-sort="num"${g ? ` data-glossary="${esc(g)}"` : ""}>${esc(h)}</th>`;
  }).join("");
  const rows = members.map((p, i) => {
    const badge = isTopProspect(p)
      ? `<span class="draft-top-badge" title="Top prospect · #${p.rank}">★ ${p.rank}</span> ` : "";
    const nameCell = p.ep_url
      ? `${badge}<a href="${esc(p.ep_url)}" rel="noopener" target="_blank">${esc(fullName(p))}</a>`
      : `${badge}${esc(fullName(p))}`;
    const statTds = statCells(p).map(([, value]) =>
      `<td class="draft-col-stat" data-value="${value !== "—" ? esc(value) : -1}">${esc(value)}</td>`).join("");
    return `<tr data-league="${esc(p.league)}">
        <td class="draft-col-rank">${i + 1}</td>
        <td class="draft-col-name">${nameCell}</td>
        <td class="draft-col-nat">${esc(p.nationality)}</td>
        <td class="draft-col-team">${esc(p.club)}</td>
        <td class="draft-col-league">${esc(leagueName(p))}</td>
        ${statTds}
      </tr>`;
  }).join("");
  return `<details class="draft-group" data-group="${code}" open>
      <summary class="draft-group-title">${esc(title)} <span class="draft-group-count">${members.length}</span></summary>
      <div class="table-scroll">
        <table class="draft-table" id="draft-table-${code}">
          <thead><tr>
            <th class="draft-col-rank" data-sort="num">#</th>
            <th class="draft-col-name" data-sort="str">Player</th>
            <th class="draft-col-nat" data-sort="str">Nat.</th>
            <th class="draft-col-team" data-sort="str">2025-26 Team</th>
            <th class="draft-col-league" data-sort="str">League</th>
            ${statHead}
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </details>`;
}

export function renderDraft(all: Prospect[]): { summary: string; controls: string; groups: string } {
  const s = summarize(all);
  const summary = `
    <ul class="draft-summary">
      <li><span class="draft-summary-n">${s["forwards"]}</span> Forwards</li>
      <li><span class="draft-summary-n">${s["defenders"]}</span> Defenders</li>
      <li><span class="draft-summary-n">${s["goalies"]}</span> Goaltenders</li>
      <li><span class="draft-summary-n">${s["leagues"]}</span> Leagues</li>
      <li><span class="draft-summary-n">${s["top_prospects"]}</span> Top prospects</li>
    </ul>`;
  const groups = POSITION_GROUPS.map(([code, title]) =>
    renderGroup(code, title, byPosition(all, code))).join("");
  return { summary, controls: renderControls(all, s["total"]!), groups };
}

function renumber(table: HTMLTableElement): void {
  let i = 0;
  table.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach((tr) => {
    if (tr.style.display !== "none") {
      i += 1;
      const rank = tr.querySelector(".draft-col-rank");
      if (rank) rank.textContent = String(i);
    }
  });
}

function wire(root: HTMLElement): void {
  const posSel = root.querySelector<HTMLSelectElement>("#draft-position-filter");
  const leagueSel = root.querySelector<HTMLSelectElement>("#draft-league-filter");
  const apply = (): void => {
    const wantPos = posSel?.value ?? "";
    const wantLeague = leagueSel?.value ?? "";
    root.querySelectorAll<HTMLElement>(".draft-group").forEach((grp) => {
      const show = !wantPos || grp.getAttribute("data-group") === wantPos;
      grp.style.display = show ? "" : "none";
      if (show) {
        (grp as HTMLDetailsElement).open = true;
        const table = grp.querySelector<HTMLTableElement>(".draft-table");
        if (!table) return;
        table.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach((tr) => {
          tr.style.display = !wantLeague || tr.getAttribute("data-league") === wantLeague ? "" : "none";
        });
        renumber(table);
      }
    });
  };
  posSel?.addEventListener("change", apply);
  leagueSel?.addEventListener("change", apply);

  root.querySelectorAll<HTMLTableElement>(".draft-table").forEach((table) => {
    table.querySelectorAll<HTMLTableCellElement>("thead th").forEach((th, col) => {
      if (th.classList.contains("draft-col-rank")) return;
      th.style.cursor = "pointer";
      let asc = false;
      th.addEventListener("click", () => {
        const numeric = th.getAttribute("data-sort") === "num";
        const tbody = table.querySelector("tbody")!;
        const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>("tbody tr"));
        asc = !asc;
        rows.sort((a, b) => {
          const ca = a.children[col] as HTMLElement, cb = b.children[col] as HTMLElement;
          if (numeric) {
            const va = parseFloat(ca.getAttribute("data-value") || ca.textContent || "") || -Infinity;
            const vb = parseFloat(cb.getAttribute("data-value") || cb.textContent || "") || -Infinity;
            return va < vb ? (asc ? -1 : 1) : va > vb ? (asc ? 1 : -1) : 0;
          }
          const va = (ca.textContent || "").trim().toLowerCase();
          const vb = (cb.textContent || "").trim().toLowerCase();
          return va < vb ? (asc ? -1 : 1) : va > vb ? (asc ? 1 : -1) : 0;
        });
        rows.forEach((r) => tbody.appendChild(r));
        renumber(table);
      });
    });
  });
}

async function loadProspects(): Promise<Prospect[]> {
  const worker = await getDbWorker();
  const sql =
    "SELECT last_name, first_name, position, nationality, club, league, ep_url, " +
    "stats_json, rank FROM expansion_prospects WHERE from_eligibility = 1 " +
    "ORDER BY sort_order, id";
  const rows = (await worker.db.query(sql)) as Array<Record<string, unknown>>;
  return rows.map((r) => ({
    last_name: String(r["last_name"] ?? ""),
    first_name: String(r["first_name"] ?? ""),
    position: String(r["position"] ?? ""),
    nationality: String(r["nationality"] ?? ""),
    club: String(r["club"] ?? ""),
    league: String(r["league"] ?? ""),
    ep_url: String(r["ep_url"] ?? ""),
    stats: r["stats_json"] ? JSON.parse(String(r["stats_json"])) : null,
    rank: r["rank"] === null || r["rank"] === undefined ? null : Number(r["rank"]),
  }));
}

export function initDraft(): boolean {
  const summaryEl = document.getElementById("draft-summary-root");
  const controlsEl = document.getElementById("draft-controls-root");
  const groupsEl = document.getElementById("draft-groups-root");
  if (!summaryEl || !controlsEl || !groupsEl) return false;

  groupsEl.innerHTML = '<p class="empty">Loading…</p>';
  void (async () => {
    try {
      const all = await loadProspects();
      const { summary, controls, groups } = renderDraft(all);
      summaryEl.innerHTML = summary;
      controlsEl.innerHTML = controls;
      groupsEl.innerHTML = groups;
      wire(document.getElementById("draft-root") ?? document.body);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      groupsEl.innerHTML = `<p class="empty">Couldn’t load the prospect board: ${esc(msg)}</p>`;
      console.error("[draft] load failed:", err);
    }
  })();
  return true;
}
